// Списки выбора: <select> и их доступные зеркала-чекбоксы с поиском и счётчиком.

import type { Territory } from '../types';
import { formatNumber as formatLocalizedNumber, t } from '../i18n';

import { controls } from './controls';
import { currentLocale } from './locale';
import { escapeHtml } from './dom';

export function selectedValues(select: HTMLSelectElement): string[] {
  const list = choiceListForSelect(select);
  if (list) {
    const optionsByValue = new Map(Array.from(select.options).map((option) => [option.value, option]));
    const checkedValues = Array.from(list.querySelectorAll<HTMLInputElement>('input[data-choice-value]:checked'))
      .map((input) => input.dataset.choiceValue ?? '')
      .filter((value) => {
        const option = optionsByValue.get(value);
        return option != null && !option.disabled;
      });
    if (checkedValues.length) {
      const selected = new Set(checkedValues);
      optionsByValue.forEach((option, value) => {
        if (!option.disabled) option.selected = selected.has(value);
      });
    }
  }
  return Array.from(select.selectedOptions)
    .filter((option) => !option.disabled)
    .map((option) => option.value);
}

export function selectedYearsFromForm(): number[] {
  return selectedValues(controls.years).map(Number).filter(Number.isFinite);
}

export function selectedMonthsFromForm(): number[] {
  return selectedValues(controls.months).map(Number).filter(Number.isFinite);
}

export function selectHasLoadingOption(select: HTMLSelectElement): boolean {
  return Array.from(select.options).some((option) =>
    option.disabled && /загрузка|loading/i.test(option.textContent ?? ''),
  );
}

export function setSelectedValues(select: HTMLSelectElement, values: Array<string | number>): void {
  const desired = new Set(values.map(String));
  Array.from(select.options).forEach((option) => {
    option.selected = desired.has(option.value);
  });
  syncChoiceMirror(select);
}

export function applyYearSelection(values: Array<string | number>): void {
  controls.yearSelectionHint.textContent = '';
  setSelectedValues(controls.years, values);
  controls.years.dispatchEvent(new Event('change', { bubbles: true }));
}

export function selectLatestYears(count: number): void {
  const years = Array.from(controls.years.options)
    .filter((option) => !option.disabled && Number.isFinite(Number(option.value)))
    .map((option) => Number(option.value))
    .sort((a, b) => b - a);
  applyYearSelection(years.slice(0, Math.min(count, years.length)));
}

export function choiceListForSelect(select: HTMLSelectElement): HTMLElement | null {
  if (select === controls.territories) return controls.territoryChoiceList;
  if (select === controls.years) return controls.yearChoiceList;
  if (select === controls.autoTerritories) return controls.autoTerritoryChoiceList;
  if (select === controls.autoYears) return controls.autoYearChoiceList;
  if (select === controls.autoDistricts) return controls.autoDistrictChoiceList;
  if (select === controls.autoRegions) return controls.autoRegionChoiceList;
  if (select === controls.autoMunicipalities) return controls.autoMunicipalityChoiceList;
  return null;
}

export function syncChoiceMirror(select: HTMLSelectElement): void {
  const list = choiceListForSelect(select);
  if (!list) return;
  const options = Array.from(select.options);
  if (!options.length) {
    list.innerHTML = '<p class="checkbox-choice-empty">Нет доступных значений</p>';
    return;
  }
  list.innerHTML = options.map((option, index) => {
    const id = `${select.id}-choice-${index}`;
    const disabled = option.disabled ? 'disabled' : '';
    const checked = option.selected ? 'checked' : '';
    const title = option.title || option.textContent || option.value;
    return `
      <label class="checkbox-choice ${option.disabled ? 'is-disabled' : ''}" for="${escapeHtml(id)}" title="${escapeHtml(title)}">
        <input id="${escapeHtml(id)}" type="checkbox" data-choice-value="${escapeHtml(option.value)}" ${checked} ${disabled} />
        <span>${escapeHtml(option.textContent || option.value)}</span>
      </label>
    `;
  }).join('');
}

export function syncChoiceMirrors(): void {
  syncChoiceMirror(controls.territories);
  syncChoiceMirror(controls.years);
  syncChoiceMirror(controls.autoTerritories);
  syncChoiceMirror(controls.autoYears);
  syncChoiceMirror(controls.autoDistricts);
  syncChoiceMirror(controls.autoRegions);
  syncChoiceMirror(controls.autoMunicipalities);
}

export function bindChoiceMirror(select: HTMLSelectElement, list: HTMLElement): void {
  list.addEventListener('change', (event) => {
    const input = (event.target as Element).closest<HTMLInputElement>('input[data-choice-value]');
    if (!input) return;
    const option = Array.from(select.options).find((candidate) => candidate.value === input.dataset.choiceValue);
    if (!option || option.disabled) return;
    option.selected = input.checked;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

export function searchNeedle(value: string): string {
  return value.toLocaleLowerCase(currentLocale() === 'ru' ? 'ru-RU' : 'en-US').replace(/\s+/g, ' ').trim();
}

export function setSelectOptions(
  select: HTMLSelectElement,
  items: Array<{ value: string; label: string; title?: string; disabled?: boolean }>,
  selected: Iterable<string> = [],
): void {
  const selectedSet = new Set(Array.from(selected).map(String));
  select.innerHTML = items.map((item) => {
    const selectedAttr = selectedSet.has(item.value) ? 'selected' : '';
    const disabledAttr = item.disabled ? 'disabled' : '';
    const title = item.title ?? item.label;
    return `<option value="${escapeHtml(item.value)}" title="${escapeHtml(title)}" ${selectedAttr} ${disabledAttr}>${escapeHtml(item.label)}</option>`;
  }).join('');
  syncChoiceMirror(select);
}

export function filterChoiceList(select: HTMLSelectElement, searchInput: HTMLInputElement | null): number {
  const list = choiceListForSelect(select);
  if (!list) return selectableOptionCount(select);
  const query = searchNeedle(searchInput?.value ?? '');
  let visible = 0;
  const optionsByValue = new Map(Array.from(select.options).map((option) => [option.value, option]));
  list.querySelectorAll<HTMLElement>('.checkbox-choice').forEach((label) => {
    const value = label.querySelector<HTMLInputElement>('input[data-choice-value]')?.dataset.choiceValue ?? '';
    const option = optionsByValue.get(value);
    const haystack = searchNeedle(`${option?.textContent ?? ''} ${option?.value ?? ''} ${option?.title ?? ''}`);
    const isVisible = !query || haystack.includes(query);
    label.hidden = !isVisible;
    if (isVisible && option && !option.disabled) visible += 1;
  });
  return visible;
}

export function selectedOrVisibleLabel(select: HTMLSelectElement, visible: number): string {
  const locale = currentLocale();
  const selected = selectedValues(select).length;
  const total = selectableOptionCount(select);
  if (selected > 0) {
    return t(locale, 'autoBuilderSelectedCount', {
      selected: formatLocalizedNumber(selected, locale, 0),
      total: formatLocalizedNumber(total, locale, 0),
    });
  }
  return t(locale, 'autoBuilderVisibleCount', {
    visible: formatLocalizedNumber(visible, locale, 0),
    total: formatLocalizedNumber(total, locale, 0),
  });
}

export function updateChoiceCounter(select: HTMLSelectElement, searchInput: HTMLInputElement | null, counter: HTMLElement): void {
  const visible = filterChoiceList(select, searchInput);
  counter.textContent = selectedOrVisibleLabel(select, visible);
}

export function setChoiceSelection(select: HTMLSelectElement, checked: boolean, visibleOnly = true): void {
  const list = choiceListForSelect(select);
  const visibleValues = new Set<string>();
  if (visibleOnly && list) {
    list.querySelectorAll<HTMLInputElement>('.checkbox-choice:not([hidden]) input[data-choice-value]').forEach((input) => {
      if (input.dataset.choiceValue) visibleValues.add(input.dataset.choiceValue);
    });
  }
  Array.from(select.options).forEach((option) => {
    if (option.disabled) return;
    if (!visibleOnly || visibleValues.has(option.value)) option.selected = checked;
  });
  syncChoiceMirror(select);
  select.dispatchEvent(new Event('change', { bubbles: true }));
}

export function optionItemsFromTerritories(territories: Territory[]): Array<{ value: string; label: string; title?: string }> {
  return territories.map((territory) => ({
    value: territory.id,
    label: territory.name,
    title: `${territory.name} · ${territory.id}`,
  }));
}

export function preserveSelectedOptions(select: HTMLSelectElement, items: Array<{ value: string }>): string[] {
  const valid = new Set(items.map((item) => item.value));
  return selectedValues(select).filter((value) => valid.has(value));
}

export function selectableOptionCount(select: HTMLSelectElement): number {
  return Array.from(select.options).filter((option) => !option.disabled && option.value !== '').length;
}

export function selectedOrAvailableCount(select: HTMLSelectElement): number {
  const selected = selectedValues(select).length;
  return selected || selectableOptionCount(select);
}
